/**
 * Resolve a free-form class name (English or Russian, any case) to a canonical
 * SRD class slug. Characters created/edited with a localized class_name like
 * "Бард" otherwise fail SRD lookups (slugified → "бард" ≠ "bard"), breaking
 * level-up ("Unknown class") and spell-slot recompute.
 */
import { srdClasses } from './srd/classes.js';

/** Russian (and common alternate) class names → SRD slug. */
const RU_CLASS_ALIASES: Record<string, string> = {
  'варвар': 'barbarian',
  'бард': 'bard',
  'жрец': 'cleric', 'клерик': 'cleric',
  'друид': 'druid',
  'воин': 'fighter', 'боец': 'fighter', 'файтер': 'fighter',
  'монах': 'monk',
  'паладин': 'paladin',
  'следопыт': 'ranger', 'рейнджер': 'ranger',
  'плут': 'rogue', 'вор': 'rogue', 'разбойник': 'rogue',
  'чародей': 'sorcerer',
  'колдун': 'warlock', 'чернокнижник': 'warlock',
  'волшебник': 'wizard', 'маг': 'wizard', 'визард': 'wizard',
};

/**
 * Returns the canonical SRD class slug for `className`, or null if it can't be
 * resolved. Tries, in order: direct slug match, RU/alias table, SRD `name`
 * (case-insensitive). Does NOT consider homebrew — callers overlay that first.
 */
export function resolveClassSlug(className: string | null | undefined): string | null {
  if (!className || typeof className !== 'string') return null;
  const trimmed = className.trim();
  if (!trimmed) return null;

  const slug = trimmed.toLowerCase().replace(/\s+/g, '-');
  if (srdClasses.some(c => c.slug === slug)) return slug;

  const aliased = RU_CLASS_ALIASES[trimmed.toLowerCase()];
  if (aliased) return aliased;

  const byName = srdClasses.find(c => c.name.toLowerCase() === trimmed.toLowerCase());
  return byName ? byName.slug : null;
}
